import useLocalStorageState from "use-local-storage-state";
import { z } from "zod";

export function createLocalStorageDef<T extends z.ZodTypeAny>(name: string, schema: T) {
  const subscribers = new Set<() => void>();
  const notifySubscribers = () => {
    subscribers.forEach((listener) => listener());
  };

  const def = {
    get: (): z.infer<T> | null => {
      const value = localStorage.getItem(name);
      if (!value) {
        return null;
      }
      const parsed = schema.safeParse(JSON.parse(value));
      if (!parsed.success) {
        return null;
      }
      return parsed.data;
    },
    set: (value: z.input<T>) => {
      localStorage.setItem(name, JSON.stringify(schema.parse(value)));
      notifySubscribers();
    },
    remove: () => {
      localStorage.removeItem(name);
      notifySubscribers();
    },
    subscribe: (listener: () => void) => {
      subscribers.add(listener);

      const handleStorage = (event: StorageEvent) => {
        if (event.key === name) {
          listener();
        }
      };

      window.addEventListener("storage", handleStorage);

      return () => {
        subscribers.delete(listener);
        window.removeEventListener("storage", handleStorage);
      };
    },
    use: (p?: { defaultValue?: z.input<T> }) => {
      return useLocalStorageState(name, {
        defaultValue: p?.defaultValue,
        serializer: {
          stringify: (value) => JSON.stringify(schema.parse(value)),
          parse: (value) => schema.parse(JSON.parse(value)),
        },
      });
    },
  };

  return def as typeof def & { $type: T };
}
